import { useRef, useState, useEffect } from 'react';

import Icon from '@klv/atoms/icon';
import useOutsideClick from '@klv/hooks/useOutsideClick';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/languageSelector.module.scss';

import type { TBackgroundColor } from '@klv/organisms/universal-footer/types';
import type { ILanguageSelector } from './types';


const hrefLangCountryAbbreviationOverrides = {
  'en-gb': 'UK',
  'ko-kr': '한국',
} as const;

const LanguageName = ({ className, countryAbbreviation, languageName, backgroundColor }: { className?: string; countryAbbreviation?: string; languageName: string; backgroundColor?: TBackgroundColor; }) => (
  <span className={className}>
    {languageName}{countryAbbreviation && (<span className={generateClassName([styles.languageCountry, backgroundColor && styles[backgroundColor]])}> - {countryAbbreviation}</span>)}
  </span>
);

const generateLanguageNameObject = ({ hrefLang, nativeName }: { hrefLang: string; nativeName: string; }) => {
  const languageNameObject: {
    countryAbbreviation?: string;
    languageName: string;
  } = {
    languageName: nativeName.split('(')[0].trim(),
  };

  if (hrefLang.includes('-')) {
    languageNameObject.countryAbbreviation =
      Object.keys(hrefLangCountryAbbreviationOverrides).includes(hrefLang)
        ? hrefLangCountryAbbreviationOverrides[hrefLang as keyof typeof hrefLangCountryAbbreviationOverrides]
        : hrefLang.split('-')[1].toUpperCase();
  }

  return languageNameObject;
};

const generateLinkList = ({
  currentLanguage,
  sites,
  translations,
}: ILanguageSelector) => sites.map((site) => {
  const { countryAbbreviation = '', languageName } = generateLanguageNameObject({ hrefLang: site.hreflang, nativeName: site.nativeName });
  const translation = translations.find(({ hreflang }) => hreflang === site.hreflang);

  if (translation) {
    return ({
      countryAbbreviation,
      current: translation.hreflang === currentLanguage || translation.hreflang === currentLanguage?.split('-')[0],
      fallback: false,
      href: translation.url,
      hrefLang: translation.hreflang,
      languageName,
    });
  }

  return ({
    countryAbbreviation,
    current: site.hreflang === currentLanguage || site.hreflang === currentLanguage?.split('-')[0],
    fallback: true,
    href: site.path,
    hrefLang: site.hreflang,
    languageName,
  });
}).sort((a, _b) => (a.current ? -1 : 0));

export const LanguageSelector = ({
  className,
  currentLanguage = 'en-us',
  sites,
  translations,
  backgroundColor = 'cotton',
  isUtilityNavigation = false,
  isDrawer = false,
  ...props
}: ILanguageSelector) => {
  const linkList = generateLinkList({ currentLanguage, sites, translations });
  const currentLink = linkList.find(({ current }) => current);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => {
    setOpen(false);
  });

  useEffect(() => {
    if (!isUtilityNavigation) return;

    const handleScroll = () => {
      if (open) {
        const rect = ref.current?.getBoundingClientRect();
        if (rect && rect.top < 0) {
          setOpen(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUtilityNavigation, open]);

  if (sites.length < 2) {
    return null;
  }

  if (!currentLink) {
    return null;
  }
  
  return (
    <div className={generateClassName([className, styles.languageSelector, backgroundColor && styles[backgroundColor], open && styles.open, isUtilityNavigation && styles.utilityNavigation, isDrawer && styles.drawer])} {...props} ref={ref}>
      <UniversalMenuItem as='button' className={styles.currentLanguage} onClick={() => setOpen((value) => !value)}>
        <Icon color='currentColor' iconName='Globe' size={1.6} />
        <LanguageName className={generateClassName([styles.currentLanguageName, isDrawer && styles.drawer])} backgroundColor={backgroundColor} countryAbbreviation={currentLink.countryAbbreviation} languageName={currentLink.languageName} />
        <Icon color='currentColor' className={styles.chevron} iconName='ChevronDown' size={0.3} />
      </UniversalMenuItem>
      <nav className={generateClassName([styles.languageMenu, isUtilityNavigation && styles.utilityNavigation, isDrawer && styles.drawer])}>
        <ul className={styles.languageList}>
          {linkList.map(({ countryAbbreviation, current, fallback, href, hrefLang, languageName }) => (
            <li key={hrefLang} className={styles.listItem}>
              <UniversalMenuItem
                aria-current={current || undefined}
                className={generateClassName([styles.link, backgroundColor && styles[backgroundColor]])}
                rel={fallback ? undefined : 'alternate'}
                href={href}
                hrefLang={hrefLang}
                lang={hrefLang}
              >
                <LanguageName countryAbbreviation={countryAbbreviation} languageName={languageName} />
                {current && (<Icon aria-label='(current language)' color='currentColor' iconName='Check' size={1.4} />)}
              </UniversalMenuItem>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
